// services/scheduleReportService.ts

import { UnreportedScheduleItem, ScheduleReportLink, ReportMemberData as CommonReportMemberData, convertScheduleMemberToReportMember } from '../types/commonSchedule';
import { RouteInfo } from '../types/address';
import { ReportData, ReportMemberData, ReportVehicleData } from '../types/dailyReport';
import { ScheduleMember } from '../types/schedule';
import api from './api';

export const scheduleReportService = {
  // 工事IDによる未報告スケジュール取得
  async getUnreportedSchedules(constructionId: number): Promise<{ 
    total: number;
    schedules: UnreportedScheduleItem[];
  }> {
    try {
      const response = await api.get(`/schedules/construction/${constructionId}/unreported`);
      return response.data;
    } catch (error) {
      console.error('未報告スケジュール取得エラー:', error);
      throw error;
    }
  },

  // スケジュールIDによるスケジュール取得
  async getScheduleById(scheduleId: number): Promise<any> {
    try {
      const response = await api.get(`/schedules/${scheduleId}`);
      return response.data;
    } catch (error) {
      console.error(`スケジュールID: ${scheduleId} の取得エラー:`, error);
      throw error;
    }
  },

  // スケジュールと日報のリンク作成
  async createScheduleReportLink(
    scheduleId: number,
    reportId: number
  ): Promise<void> {
    try {
      await api.post('/schedules/link-report', {
        scheduleId,
        reportId
      });
    } catch (error) {
      console.error('スケジュール-日報リンク作成エラー:', error);
      throw error;
    }
  },

  // URLパラメータからスケジュールデータを取得する関数
  async getScheduleDataFromParams(
    scheduleId: string, 
    supervisor: string
  ): Promise<{
    scheduleData: any;
    memberData: ReportMemberData[];
    vehicleData: ReportVehicleData[];
  }> {
    try {
      // スケジュールデータを取得
      const response = await api.get(`/schedules/${scheduleId}`);
      const scheduleData = response.data;
      
      // メンバーデータの変換
      const memberData = scheduleData.memberData?.map((member: any, index: number) => {
        let memberName = '';
        let categoryId = null;
        
        if (member.Member_ID) {
          // 自社メンバーの場合
          memberName = member.LastName || '';
          categoryId = member.CategoryID;
        } else if (member.VendorMember_ID) {
          // 外注メンバーの場合
          if (member.FormattedVendorMemberName) {
            // バックエンドから提供されるフォーマット済み名前を使用
            memberName = member.FormattedVendorMemberName;
          } else if (member.VendorName && member.VendorMemberName) {
            // フォーマット済み名前がない場合は生成
            memberName = `${member.VendorName} - ${member.VendorMemberName}`;
          } else {
            memberName = member.VendorMemberName || '不明なメンバー';
          }
          categoryId = member.CategoryID;
        }
        
        const isSupervisor = memberName === supervisor;
        
        return {
          memberOrder: isSupervisor ? 1 : index + (supervisor ? 2 : 1),
          memberName: memberName,
          isAllDate: 1,
          workType: 0,
          overtimeHours: 0,
          isCountTarget: 1.00,
          categoryId: categoryId, // カテゴリIDを設定
          memberId: member.Member_ID || null, // 社員のときだけ社員IDを保持（外注は null）
          baseUnitPrice: 0, // 後で計算されます
          calculatedUnitPrice: 0,
          overtimePay: 0,
          nightShiftPay: 0
        };
      }) || [];
      
      // 車両データの変換
      const vehicleData = scheduleData.vehicleData?.map((vehicle: any, index: number) => {
        return {
          vehicleOrder: index + 1,
          carId: vehicle.Car_ID,
          unitPrice: vehicle.Vehicle_Cost || 0, // 車両コストを使用
          mileage: 0, // 日報フォームで計算されます
          tollFee: 0 // 日報フォームで計算されます
        };
      }) || [];
      
      return {
        scheduleData,
        memberData,
        vehicleData
      };
    } catch (error) {
      console.error(`スケジュールID: ${scheduleId} のデータ取得エラー:`, error);
      throw new Error(`スケジュールデータの取得に失敗しました: ${error}`);
    }
  },
  
  
  // 日報作成後にスケジュールと関連付ける関数
  async linkReportToSchedule(
    reportId: number, 
    scheduleId: number
  ): Promise<void> {
    try {
      await api.post('/schedules/link-report', {
        scheduleId,
        reportId
      });
    } catch (error) {
      console.error(`スケジュールID: ${scheduleId} と日報ID: ${reportId} の関連付けエラー:`, error);
      throw new Error(`スケジュールと日報の関連付けに失敗しました: ${error}`);
    }
  },

  // スケジュールデータから日報データへの変換
  convertScheduleToReport(
    schedule: UnreportedScheduleItem,
    supervisor: string
  ): Partial<ReportData> {
    if (!schedule) return {};

    // 基本データの設定
    const reportData: Partial<ReportData> = {
      date: schedule.scheduleDate,
      constructionId: schedule.constructionId,
      supervisor: supervisor,
      memberData: [],
      vehicleData: []
    };

    // メンバーデータの変換
    if (schedule.memberData && schedule.memberData.length > 0) {
      reportData.memberData = schedule.memberData.map((member, index) => {
        // メンバー名を決定
        let memberName = '';

        if (member.Member_ID && member.LastName) {
          // 自社メンバーの場合
          memberName = member.LastName;
        } else if (member.VendorMember_ID) {
          // 外注メンバーの場合
          if (member.FormattedVendorMemberName) {
            // 既にフォーマット済みの名前がある場合はそれを使用
            memberName = member.FormattedVendorMemberName;
          } else if (member.VendorName && member.VendorMemberName) {
            // フォーマット済み名前がない場合は生成
            memberName = `${member.VendorName} - ${member.VendorMemberName}`;
          } else {
            // 最後の手段
            memberName = member.VendorMemberName || '不明なメンバー';
          }
        } else {
          // どちらでもない場合はdisplayNameを使用
          memberName = member.displayName || '不明なメンバー';
        }
        
        // 職長に選ばれたメンバーは最初に配置
        const isSupervisor = memberName === supervisor;
        const memberOrder = isSupervisor ? 1 : index + (supervisor ? 2 : 1);
        
        // 基本的なメンバーデータを設定
        const reportMember: ReportMemberData = {
          memberOrder: memberOrder,
          memberName: memberName,
          isAllDate: 1,
          workType: 0,
          overtimeHours: 0,
          isCountTarget: 1.00,
          categoryId: member.CategoryID, // カテゴリIDを設定
          memberId: member.Member_ID || null, // 社員のときだけ社員IDを保持（外注は null）
          baseUnitPrice: 0,       // 日報作成時に計算
          calculatedUnitPrice: 0, // 日報作成時に計算
          overtimePay: 0,
          nightShiftPay: 0
        };
        
        return reportMember;
      });
    }

    // 車両データの変換（ルート情報を使用）
    if (schedule.vehicleData && schedule.vehicleData.length > 0 && schedule.routeInfo) {
      reportData.vehicleData = schedule.vehicleData.map((vehicle, index) => {
        // 車両カテゴリに応じた料金設定
        let tollFee = 0;
        
        // 車両の種類に応じて適切な料金を設定
        if (schedule.routeInfo) {
          // 車両IDに基づいて車両カテゴリを判定するロジックが必要
          // 現時点では単純にStandard_Vehicle_Tollを使用
          tollFee = schedule.routeInfo.Standard_Vehicle_Toll || 0;
        }

        // 基本的な車両データを設定
        const reportVehicle: ReportVehicleData = {
          vehicleOrder: index + 1,
          carId: vehicle.Car_ID,
          unitPrice: 0, // 日報画面で計算される
          mileage: schedule.routeInfo ? Math.ceil(Number(schedule.routeInfo.Distance) / 1000) : 0,
          tollFee: tollFee
        };
        
        return reportVehicle;
      });
    }

    return reportData;
  },

  // 車両の種類に応じた料金を取得するヘルパー関数
  getVehicleTollFee(routeInfo: RouteInfo, carCategory?: number): number {
    if (!routeInfo) return 0;

    switch (carCategory) {
      case 1: // 大型
        return routeInfo.Large_Vehicle_Toll;
      case 2: // 中型
        return routeInfo.Medium_Vehicle_Toll;
      case 3: // 普通
        return routeInfo.Standard_Vehicle_Toll;
      case 4: // 軽
        return routeInfo.Light_Vehicle_Toll;
      default:
        return routeInfo.Standard_Vehicle_Toll; // デフォルトは普通車
    }
  }
};